import {
  ATOMIC_CHIP_CARET_MARKER,
  ensureAtomicChipCaretSpacers,
  handlePlainArrowAroundAtomicChipBoundary,
  selectAtomicChipForNativeDeletion,
  stripAtomicChipCaretMarkers,
} from '../contentEditableAtomicChipCaret';
import { tokenize } from './tokenize';

// Browsers render/position the caret unreliably right at the DOM boundary of a
// `contenteditable="false"` island. Each chip therefore owns one invisible trailing text node.
// Typed text stays in that node after the marker, preserving the browser's live selection rather
// than rewriting the active text node after every input event.
const CARET_MARKER = ATOMIC_CHIP_CARET_MARKER;

export const isChipElement = (node: Node | null): node is HTMLElement =>
  !!node && node instanceof HTMLElement && node.dataset.var !== undefined;

// Desktop browsers keep a `contenteditable="false"` chip fully atomic — the caret can never land
// inside its own text. Mobile browsers are looser about this (a long-press "select" gesture can
// place the caret/selection inside the chip's text anyway), so any active-token detection running
// on that text would treat the chip's own `{{name}}` as if the user were typing a new one,
// corrupting it (e.g. `{{vari{{variable}}able}}`). Callers must check this before trusting a
// caret position.
export const isInsideChip = (node: Node | null): boolean => {
  const element = node instanceof Element ? node : node?.parentElement ?? null;
  return !!element?.closest('[data-var]');
};

export const ensureCaretSlots = (root: HTMLElement): void => {
  ensureAtomicChipCaretSpacers({ root, isChip: isChipElement });
};

export const handlePlainArrowAroundChipBoundary = (
  key: 'ArrowLeft' | 'ArrowRight',
  selection: Selection | null,
  editor: HTMLElement | null,
): boolean => handlePlainArrowAroundAtomicChipBoundary({ key, selection, editor, isChip: isChipElement });

// Select a chip and only its invisible trailing marker, then let the browser execute Backspace or
// Delete. Native deletion keeps the edit in the browser's undo/redo history.
export const selectChipForNativeDeletion = (
  direction: 'backward' | 'forward',
  selection: Selection | null,
  editor: HTMLElement | null,
): boolean => selectAtomicChipForNativeDeletion({ direction, selection, editor, isChip: isChipElement });

// Strips caret markers out of `text`, translating `caretPos` (an offset into the raw,
// marker-containing text) into the equivalent offset in the cleaned text.
export const stripCaretMarkers = stripAtomicChipCaretMarkers;

// Serializes the editor's DOM back into the `{{variable}}` template string: text nodes contribute
// their literal text (caret markers stripped out), chip elements (`[data-var]`) contribute
// `{{name}}`. Keeps the contentEditable editor's DOM as the source of truth for the string handed
// to `onChange`.
export const serialize = (root: HTMLElement): string => {
  let result = '';

  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += (node.textContent ?? '').replaceAll(CARET_MARKER, '');
    } else if (node instanceof HTMLElement) {
      const variableName: unknown = node.dataset.var;
      if (typeof variableName === 'string') result += `{{${variableName}}}`;
    }
  });

  return result;
};

// Builds a single atomic `[data-var]` chip span, keyboard-focusable and with an accessible name
// derived from its tooltip description. Shared by every path that creates a chip, the dropdown/
// hand-typed-completion paths in TokenInput, and `buildSegmentNodes` below so a chip is equally
// accessible whether it was just inserted or rendered from an existing/pasted value.
export const createChipElement = (
  variableName: string,
  isKnownVariable: (name: string) => boolean,
  getChipTooltip?: (name: string) => string | undefined,
): HTMLSpanElement => {
  const chip = document.createElement('span');
  chip.dataset.var = variableName;
  chip.contentEditable = 'false';
  chip.tabIndex = 0;
  chip.textContent = `{{${variableName}}}`;
  if (!isKnownVariable(variableName)) chip.dataset.unsupported = 'true';
  const tooltip = getChipTooltip?.(variableName);
  if (tooltip) {
    chip.dataset.tooltip = tooltip;
    chip.setAttribute('aria-label', tooltip);
  }
  return chip;
};

// Builds the DOM nodes for a template string: literal segments become text nodes, variable
// segments become atomic `[data-var]` chip spans. `isKnownVariable` flags a chip as unsupported
// (styled differently) when its name isn't among the variables the consumer configured. Shared by
// `renderInto` (rebuilding the whole editor) and pasted-text handling (splicing recognized
// variables in as real chips instead of leaving pasted `{{name}}` text literal).
export const buildSegmentNodes = (
  template: string,
  isKnownVariable: (name: string) => boolean,
  getChipTooltip?: (name: string) => string | undefined,
): ChildNode[] =>
  tokenize(template).map((segment) => {
    const rawValue: unknown = segment.value;
    const value = typeof rawValue === 'string' ? rawValue : '';
    if (segment.type === 'literal') return document.createTextNode(value);

    return createChipElement(value, isKnownVariable, getChipTooltip);
  });

// (Re)builds the editor's DOM from a template string, each chip followed by a caret marker. Only
// called on mount and when an external value change is detected (see `TokenInput`), so it never
// fights the user's caret while they're actively typing.
export const renderInto = (
  root: HTMLElement,
  template: string,
  isKnownVariable: (name: string) => boolean,
  getChipTooltip?: (name: string) => string | undefined,
): void => {
  root.innerHTML = '';
  buildSegmentNodes(template, isKnownVariable, getChipTooltip).forEach((node) => root.appendChild(node));
  ensureCaretSlots(root);
};
